import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Building2, Calendar, Repeat, Sparkles, Users, Flame,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { getLocationByName } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import { humanizeInterval, relativeDays, formatDate, compactNumber, MONTH_LABELS } from '../utils/format';
import { cityInfo, elderlyCount, REGION_COLORS } from '../data/cityReference';
import StatTile from '../components/ui/StatTile';
import SectionHeader from '../components/ui/SectionHeader';
import EmptyState from '../components/ui/EmptyState';
import LocationResearch from '../components/LocationResearch';
import { SkeletonCard, Skeleton } from '../components/ui/Skeleton';

function RhythmBadge({ rhythm }) {
  if (!rhythm) return null;
  const tone = rhythm.isRegular
    ? 'bg-accent-100 text-accent-700'
    : rhythm.label === 'One-off'
      ? 'bg-ink-100 text-ink-500'
      : 'bg-amber-100 text-amber-800';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${tone}`}>
      <Repeat className="w-3 h-3" /> {rhythm.label}
    </span>
  );
}

function LocationProfile() {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getLocationByName(name)
      .then((res) => { if (active) { setData(res.data.data); setError(null); document.title = `${res.data.data.location} | Dental Events`; } })
      .catch((err) => { if (active) setError(getApiErrorMessage(err, 'Failed to load location')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; document.title = 'Dental Events'; };
  }, [name]);

  const chartData = data?.monthly?.map((m) => ({ month: MONTH_LABELS[m.month - 1], count: m.count })) || [];
  const saturation = data ? data.summary.clinicCount : 0;
  const info = data ? cityInfo(data.location) : null;

  return (
    <div className="space-y-5 sm:space-y-6 animate-fade-in-up">
      <Link to="/map" className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-800">
        <ArrowLeft className="w-4 h-4" /> Map & locations
      </Link>

      {loading ? (
        <>
          <Skeleton className="h-10 w-1/2" />
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
          <SkeletonCard />
        </>
      ) : error ? (
        <div className="card bg-red-50 ring-red-200"><p className="text-red-700">{error}</p></div>
      ) : data ? (
        <>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-accent-600 text-white flex items-center justify-center shrink-0">
              <MapPin className="w-6 h-6" />
            </div>
            <div className="min-w-0">
              <h1 className="page-title truncate">{data.location}</h1>
              <p className="text-sm text-ink-500">
                {data.summary.clinicCount} competing clinic{data.summary.clinicCount === 1 ? '' : 's'} · {data.summary.totalEvents} events
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <StatTile icon={Calendar} label="Total events" value={data.summary.totalEvents} tone="dental" />
            <StatTile icon={Users} label="Competing clinics" value={data.summary.clinicCount} tone="accent" />
            <StatTile icon={Flame} label="Saturation" value={saturation >= 5 ? 'High' : saturation >= 2 ? 'Medium' : 'Low'} subValue={`${data.summary.totalEvents} events / ${data.summary.clinicCount} clinics`} tone={saturation >= 5 ? 'red' : saturation >= 2 ? 'amber' : 'green'} />
          </div>

          {info && (
            <div className="card">
              <SectionHeader
                icon={Users}
                title="Local profile"
                subtitle="Approximate reference data (Census-2021 era)"
                action={
                  <span className="chip" style={{ background: `${REGION_COLORS[info.region]}22`, color: REGION_COLORS[info.region] }}>
                    {info.region}
                  </span>
                }
              />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-ink-500">District</p>
                  <p className="font-semibold text-ink-900 text-sm">{info.district}</p>
                </div>
                <div>
                  <p className="text-xs text-ink-500">Population</p>
                  <p className="font-semibold text-ink-900">{compactNumber(info.population)}</p>
                </div>
                <div>
                  <p className="text-xs text-ink-500">Aged 65+</p>
                  <p className="font-semibold text-ink-900">{info.elderlyPercent}% <span className="text-xs font-normal text-ink-400">(~{compactNumber(elderlyCount(info))})</span></p>
                </div>
                <div>
                  <p className="text-xs text-ink-500">Dentists~</p>
                  <p className="font-semibold text-ink-900">{info.dentists?.toLocaleString() ?? '—'}</p>
                </div>
              </div>
              {info.dentists && elderlyCount(info) && (
                <p className="text-xs text-ink-400 mt-3">
                  ~{Math.round(elderlyCount(info) / info.dentists).toLocaleString()} residents aged 65+ per dentist — an ageing-demand vs supply indicator.
                </p>
              )}
            </div>
          )}

          <div className="card">
            <SectionHeader icon={Calendar} title="Seasonality" subtitle="When competitors run events here across the year" />
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 6, right: 6, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: '#f1f5f9' }} contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
                  <Bar dataKey="count" fill="#6366f1" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div>
            <SectionHeader icon={Building2} title="Who competes here" subtitle="Clinics targeting this location, by visit frequency" />
            {data.clinics.length === 0 ? (
              <EmptyState icon={Building2} title="No clinics yet" />
            ) : (
              <div className="card p-0 overflow-hidden divide-y divide-ink-100">
                {data.clinics.map((c) => (
                  <Link
                    key={c.clinicName}
                    to={`/clinics/${encodeURIComponent(c.clinicName)}`}
                    className="flex items-center gap-3 p-4 hover:bg-ink-50 transition-colors"
                  >
                    <div className="w-9 h-9 rounded-xl bg-dental-50 text-dental-600 ring-1 ring-dental-100 flex items-center justify-center shrink-0 font-bold text-sm">
                      {c.clinicName?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-ink-900 truncate">{c.clinicName}</p>
                      <p className="text-xs text-ink-500">
                        {c.visitCount}× · {humanizeInterval(c.avgIntervalDays)} · last {relativeDays(c.daysSinceLast)}
                      </p>
                    </div>
                    <RhythmBadge rhythm={c.rhythm} />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {data.upcoming?.length > 0 && (
            <div className="card">
              <SectionHeader icon={Sparkles} title="Upcoming here" />
              <ul className="space-y-2">
                {data.upcoming.map((e) => (
                  <li key={e.id} className="flex items-center gap-3 text-sm border-t border-ink-100 pt-2 first:border-0 first:pt-0">
                    <Calendar className="w-4 h-4 text-dental-500 shrink-0" />
                    <span className="font-medium text-ink-800 truncate flex-1">{e.title || 'Event'}</span>
                    <Link to={`/clinics/${encodeURIComponent(e.clinicName)}`} className="text-xs text-dental-600 hover:underline truncate max-w-[40%]">{e.clinicName}</Link>
                    <span className="text-xs text-ink-500 shrink-0">{formatDate(e.startDate)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <LocationResearch name={data.location} />
        </>
      ) : null}
    </div>
  );
}

export default LocationProfile;
